// src/utils/reminderDate.js — orgtime-7c2e4b19
//
// THE job-reminder default: when "remind the office to confirm this job" falls, from the company's
// own setting, organizations/{orgId}.jobReminderSettings (Company Settings → job reminders):
//   mode '2_weeks' (the default) · '3_weeks' · 'custom' (customDays, default 14) · '1st_prior_month'
// Counted in COMPANY days (tz = the company's timezone; no zone → Toronto) and returned as the
// company's MIDNIGHT on that day, which the server's daily reminder check finds on that day
// (scheduledJobReminderCheck; testOrgTime.js R1 pins why that holds).
//
// ONE RULE, SET IN ONE PLACE (Marcus, 2026-10-18): the phone follows this too. Before update 5 the
// phone used its own reminderDaysBefore (default 1 day). ⚠️ TWO COPIES, byte-identical:
// src/utils/reminderDate.js (web) and spatchy-mobile/src/utils/reminderDate.js (phone), pinned by
// functions/scripts/testOrgTime.js. Change both.
//
// Returns null when there is no usable job date.
//
// remopen-3f8b1d62 — OPENING A SAVED JOB SHOWS ITS SAVED REMINDER. Both editors keep the reminder in
// step with the job's start (and, on web, its type), so a new start gets a new default. Opening a job
// changes those values too (they load in), and that used to look like an edit: the saved date was
// replaced by the default every time the job was opened, and on web a reminder turned OFF came back
// ON. A guard made when the job is opened (openReminderGuard) holds the saved reminder until the user
// really changes the start or type (reminderFollowsChange):
//   - the job as opened → false (keep the saved reminder);
//   - a render from before the job's own values arrived → false;
//   - anything else, once the opened values have been seen → true from then on (follow the change).
// No guard (a new job, or nothing saved to protect) → always true, the old behaviour.
import { orgYmd, orgDayStart, ymdAddDays } from './orgTime';

export const calculateReminderDate = (startAt, reminderSettings, tz) => {
    if (!startAt) return null;
    const jobDate = typeof startAt.toDate === 'function' ? startAt.toDate() : new Date(startAt);
    if (!jobDate || isNaN(jobDate.getTime())) return null;

    const mode = reminderSettings?.mode || '2_weeks';
    const customDays = parseInt(reminderSettings?.customDays, 10) || 14;
    const jobYmd = orgYmd(jobDate, tz);
    let reminderYmd;
    switch (mode) {
        case '1st_prior_month': {
            const [y, m] = jobYmd.split('-').map(Number);
            reminderYmd = `${m === 1 ? y - 1 : y}-${String(m === 1 ? 12 : m - 1).padStart(2, '0')}-01`;
            break;
        }
        case '3_weeks':
            reminderYmd = ymdAddDays(jobYmd, -21);
            break;
        case 'custom':
            reminderYmd = ymdAddDays(jobYmd, -customDays);
            break;
        case '2_weeks':
        default:
            reminderYmd = ymdAddDays(jobYmd, -14);
            break;
    }
    return orgDayStart(reminderYmd, tz);
};

// remopen-3f8b1d62 — see the header.
export const reminderInputsKey = (startAt, eventType) => {
    const d = startAt && typeof startAt.toDate === 'function' ? startAt.toDate() : (startAt ? new Date(startAt) : null);
    const t = d ? d.getTime() : NaN;
    return `${eventType || ''}|${Number.isFinite(t) ? t : ''}`;
};
export const openReminderGuard = (startAt, eventType) => ({ key: reminderInputsKey(startAt, eventType), seen: false, released: false });
export const reminderFollowsChange = (guard, startAt, eventType) => {
    if (!guard || guard.released) return true;
    if (reminderInputsKey(startAt, eventType) === guard.key) { guard.seen = true; return false; }
    if (!guard.seen) return false;
    guard.released = true;
    return true;
};
